import { CheckIcon } from "@radix-ui/react-icons";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";

import { cva, VariantProps } from "class-variance-authority";
import { radioClassName } from "../../styles/input";
import { cn } from "../../utils";
import { buttonVariants } from "./Button";
import { navigationMenuTriggerStyle } from "./NavigationMenu";

const radioGroupVariants = cva("", {
  variants: {
    variant: {
      default: "grid gap-0",
      buttons:
        "flex max-w-full gap-2 rounded-lg border border-gray-200 bg-gray-100 p-1",
      cards: "flex gap-2",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type RadioGroupProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
> &
  VariantProps<typeof radioGroupVariants>;

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, variant, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn(radioGroupVariants({ variant, className }))}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva("", {
  variants: {
    variant: {
      default: cn(
        "border-primary text-primary focus-visible:ring-ring aspect-square size-4 rounded-full border shadow focus:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
        radioClassName
      ),
      buttons: cn(
        buttonVariants({ variant: "ghost" }),
        navigationMenuTriggerStyle(),
        "data-[state=checked]:text-primary-500 data-[state=checked]:bg-white data-[state=checked]:font-extrabold"
      ),
      cards: cn(
        "group flex flex-1 items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3",
        "cursor-pointer transition-colors",
        "hover:border-gray-300",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        "disabled:cursor-not-allowed disabled:opacity-50"
      ),
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type RadioGroupItemProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> &
  VariantProps<typeof radioGroupItemVariants> & {
    label?: string;
  };

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant, label, ...props }, ref) => {
  if (variant === "cards") {
    return (
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(radioGroupItemVariants({ variant, className }))}
        {...props}
      >
        <span className="text-sm font-medium text-gray-700">
          {label ?? props.value}
        </span>
        <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-data-[state=checked]:border-primary-500 group-data-[state=checked]:bg-primary-500">
          <RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-white" />
        </span>
      </RadioGroupPrimitive.Item>
    );
  }

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ variant, className }))}
      {...props}
    >
      {variant !== "buttons" ? (
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <CheckIcon className="fill-primary size-4" />
        </RadioGroupPrimitive.Indicator>
      ) : (
        (label ?? props.value)
      )}
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
